import { AssetType } from "../../../shared/types/AssetType";
import { OrderState } from "../../../shared/types/OrderState";
import { AutoOrderConfigurationModel } from "./AutoOrderConfigurationModel";

// todo: rename to OrderModel
export class PortfolioItemModel {
    private readonly _initialHashCode: number;
    private _isChanged: boolean;

    private _isActive = false;
    private _state = OrderState.Disabled;
    
    externalId: number;
    exchangeOrderId: number | null;
    internalId: string;
    autoOrderConfiguration: AutoOrderConfigurationModel | null;
    locked: boolean;
    type: AssetType;
    strike: number | null;
    expireAt: Date | null;
    customImpliedVolatility: number | null;
    symbol: string;
    size: number;
    sizeStep: number | null;
    openPrice: number;
    markPrice: number;
    markImpliedVolatility: number | null;
    openedPositionAt: Date;
    impliedVolatilityAtOpen: number | null;
    underlyingPriceAtOpen: number | null;
    delta: number;
    gamma: number | null;
    vega: number | null;
    theta: number | null;
    comment: string | null;
    expirationPrice: number | null;
    dateForPricing: Date;
    customImpliedVolatilityStep: number | null;
    impliedVolatilityForPricing?: number | null;
    underlyingPriceForPricing: number | null;

    constructor(data: {
        externalId: number;
        exchangeOrderId: number | null;
        internalId: string;
        autoOrderConfiguration?: AutoOrderConfigurationModel | null;
        state: OrderState;
        isActive: boolean;
        locked: boolean;
        type: AssetType;
        strike: number | null;
        expireAt: Date | null;
        customImpliedVolatility: number | null;
        symbol: string;
        size: number;
        sizeStep?: number | null;
        openPrice: number;
        markPrice: number;
        markImpliedVolatility: number | null;
        openedPositionAt: Date;
        impliedVolatilityAtOpen: number | null;
        underlyingPriceAtOpen: number | null;
        delta: number;
        gamma: number | null;
        vega: number | null;
        theta: number | null;
        comment: string | null;
        expirationPrice: number | null;
        dateForPricing: Date;
        customImpliedVolatilityStep?: number | null;
        impliedVolatilityForPricing?: number | null;
        underlyingPriceForPricing: number | null;
    }) {
        this.externalId = data.externalId;
        this.exchangeOrderId = data.exchangeOrderId;
        this.internalId = data.internalId;
        this.autoOrderConfiguration = data.autoOrderConfiguration ?? null;
        this._state = data.state;
        this._isActive = data.isActive;
        this.locked = data.locked;
        this.openedPositionAt = data.openedPositionAt;
        this.type = data.type;
        this.strike = data.strike;
        this.expireAt = data.expireAt;
        this.impliedVolatilityAtOpen = data.impliedVolatilityAtOpen;
        this.customImpliedVolatility = data.customImpliedVolatility;
        this.customImpliedVolatilityStep = data.customImpliedVolatilityStep ?? null;
        this.symbol = data.symbol;
        this.size = data.size;
        this.sizeStep = data.sizeStep ?? null;
        this.openPrice = data.openPrice;
        this.markPrice = data.markPrice;
        this.markImpliedVolatility = data.markImpliedVolatility;
        this.underlyingPriceAtOpen = data.underlyingPriceAtOpen;
        this.delta = data.delta;
        this.gamma = data.gamma;
        this.vega = data.vega;
        this.theta = data.theta;
        this.comment = data.comment;
        this.expirationPrice = data.expirationPrice;
        this.dateForPricing = data.dateForPricing;
        this.impliedVolatilityForPricing = data.impliedVolatilityForPricing;
        this.underlyingPriceForPricing = data.underlyingPriceForPricing;

        this._isChanged = false;
        this._initialHashCode = this.getHashCode();
    }

    get state(): OrderState {
        return this._state;
    }

    set state(value: OrderState) {
        this._state = value;
        this.setChangedState();
    }
    
    get isActive(): boolean {
        return this._isActive;
    }

    set isActive(value: boolean) {
        this._isActive = value;
        this.setChangedState();
    }
    
    setChangedState = (): void => {
        this._isChanged = this._initialHashCode !== this.getHashCode();
    }

    resetChangedState = (): void => {
        this._isChanged = false;
    }
    
    get isChanged(): boolean {
        return this._isChanged;
    } 

    // todo: move to HashCodeProcessor
    private getHashCode = (): number => {
        let hashCode = this.getFloatHashCode(this._state);
        hashCode = this.combineHashCode(hashCode, this.getBooleanHashCode(this._isActive));
        hashCode = this.combineHashCode(hashCode, this.getFloatHashCode(this.size));
        hashCode = this.combineHashCode(hashCode, this.getFloatHashCode(this.sizeStep));
        hashCode = this.combineHashCode(hashCode, this.getFloatHashCode(this.strike));
        hashCode = this.combineHashCode(hashCode, this.getDateHashCode(this.expireAt));
        
        return hashCode;
    }

    private combineHashCode = (hashCode: number, value: number): number => {
        const prime = 7;
        return (hashCode * prime + value) | 0;
    }
    
    private getFloatHashCode = (value: number | null): number => {
        if (value === null) return 0;
        
        const buffer = new ArrayBuffer(8);
        const view = new DataView(buffer);
        view.setFloat64(0, value, true);
        
        const low = view.getUint32(0, true);
        const high = view.getUint32(4, true);
        
        return ((low * 31) ^ high) | 0;
    }

    private getDateHashCode = (value: Date | null): number => {
        if (value === null) return 0;
        return this.getFloatHashCode(new Date(value).getTime());
    }

    private getBooleanHashCode = (value: boolean): number => {
        return value ? 1231 : 1237;
    }
}

